import React, { useState, useEffect } from 'react';
import { ref as dbRef, onValue, push, remove } from 'firebase/database';
import { db } from '../../firebase';
import { Course } from '../../types';

const CURRENT_SEMESTER = '2025-2026';

const emptyForm = { code: '', name: '', credits: 3, instructor: '', schedule: '', semester: CURRENT_SEMESTER };

interface CourseLink {
  title: string;
  url: string;
  addedAt: number;
}

const CoursesTab: React.FC = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [courseLinks, setCourseLinks] = useState<Record<string, CourseLink[]>>({});
  const [linkForm, setLinkForm] = useState({ title: '', url: '' });
  const [addingLink, setAddingLink] = useState(false);

  useEffect(() => {
    return onValue(dbRef(db, 'courses'), snap => {
      const d = snap.val();
      const list: Course[] = d ? Object.entries(d).map(([id, v]) => ({ id, ...(v as Course) })) : [];
      setCourses(list);
      list.forEach(c => {
        if (!c.id) return;
        onValue(dbRef(db, `courseLinks/${c.id}`), s => {
          const ld = s.val();
          setCourseLinks(prev => ({ ...prev, [c.id!]: ld ? Object.values(ld) as CourseLink[] : [] }));
        });
      });
    });
  }, []);

  const set_ = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(f => ({ ...f, [field]: e.target.value }));

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    await push(dbRef(db, 'courses'), { ...form, credits: Number(form.credits) });
    setForm(emptyForm);
    setAdding(false);
    setSaving(false);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Bu dersi silmek istediğinizden emin misiniz?')) {
      await remove(dbRef(db, `courses/${id}`));
    }
  };

  const handleAddLink = async (courseId: string) => {
    if (!linkForm.title.trim() || !linkForm.url.trim()) return;
    const link: CourseLink = { title: linkForm.title.trim(), url: linkForm.url.trim(), addedAt: Date.now() };
    await push(dbRef(db, `courseLinks/${courseId}`), link);
    setLinkForm({ title: '', url: '' });
    setAddingLink(false);
  };

  const handleDeleteLink = async (courseId: string, linkUrl: string) => {
    const snap = await new Promise<any>(res => onValue(dbRef(db, `courseLinks/${courseId}`), res, { onlyOnce: true }));
    const entries: any[] = snap.val() ? Object.entries(snap.val()) : [];
    const entry = entries.find(([, v]: any) => v.url === linkUrl);
    if (entry) await remove(dbRef(db, `courseLinks/${courseId}/${entry[0]}`));
  };

  const isYouTube = (url: string) => url.includes('youtube.com') || url.includes('youtu.be');

  return (
    <>
      <h2 className="admin-page-title">Ders Yönetimi</h2>
      <p className="admin-page-sub">{courses.length} ders kayıtlı</p>

      {!adding ? (
        <button className="a-btn a-btn-add" onClick={() => setAdding(true)}>+ Yeni Ders Ekle</button>
      ) : (
        <div className="admin-card a-form-card">
          <div className="admin-card-header"><h3>Yeni Ders</h3></div>
          <form onSubmit={handleAdd} className="a-form">
            <div className="a-form-row">
              <div className="a-form-group">
                <label>Ders Kodu</label>
                <input value={form.code} onChange={set_('code')} placeholder="BUS101" required />
              </div>
              <div className="a-form-group a-form-group-wide">
                <label>Ders Adı</label>
                <input value={form.name} onChange={set_('name')} placeholder="Business Administration" required />
              </div>
              <div className="a-form-group a-form-group-sm">
                <label>Kredi</label>
                <input type="number" value={form.credits} onChange={set_('credits')} min={1} max={6} required />
              </div>
            </div>
            <div className="a-form-row">
              <div className="a-form-group">
                <label>Öğretim Üyesi</label>
                <input value={form.instructor} onChange={set_('instructor')} placeholder="Prof. Dr. John Smith" required />
              </div>
              <div className="a-form-group">
                <label>Program (Gün/Saat)</label>
                <input value={form.schedule} onChange={set_('schedule')} placeholder="Pzt/Çrş 10:00-11:30" required />
              </div>
              <div className="a-form-group">
                <label>Dönem</label>
                <input value={form.semester} onChange={set_('semester')} required />
              </div>
            </div>
            <div className="a-form-actions">
              <button type="submit" className="a-btn a-btn-save" disabled={saving}>
                {saving ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
              <button type="button" className="a-btn a-btn-cancel" onClick={() => { setAdding(false); setForm(emptyForm); }}>
                İptal
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="admin-card">
        <div className="a-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Kod</th><th>Ders Adı</th><th>Kredi</th>
                <th>Öğretim Üyesi</th><th>Program</th><th>Dönem</th><th>Materyal</th><th></th>
              </tr>
            </thead>
            <tbody>
              {courses.length === 0 ? (
                <tr><td colSpan={8} className="a-empty">Kayıtlı ders bulunmamaktadır.</td></tr>
              ) : courses.map(c => (
                <React.Fragment key={c.id}>
                  <tr>
                    <td className="a-code-cell">{c.code}</td>
                    <td className="a-name-cell">{c.name}</td>
                    <td>{c.credits}</td>
                    <td>{c.instructor}</td>
                    <td>{c.schedule}</td>
                    <td>{c.semester}</td>
                    <td>
                      <span className="a-material-count">{courseLinks[c.id!]?.length || 0} link</span>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="a-btn a-btn-detail" onClick={() => {
                        setExpandedId(expandedId === c.id ? null : c.id!);
                        setAddingLink(false);
                        setLinkForm({ title: '', url: '' });
                      }}>
                        {expandedId === c.id ? 'Kapat' : 'Materyal'}
                      </button>
                      <button className="a-btn a-btn-delete" style={{ marginLeft: 4 }} onClick={() => handleDelete(c.id!)}>Sil</button>
                    </td>
                  </tr>

                  {expandedId === c.id && (
                    <tr>
                      <td colSpan={8} style={{ padding: 0 }}>
                        <div className="a-material-panel a-material-panel-single">
                          <div className="a-material-section-title">🔗 Materyal Linkleri (YouTube, Drive, PDF linki vb.)</div>

                          {!addingLink ? (
                            <button className="a-btn a-btn-add" style={{ margin: '6px 0 10px', padding: '5px 14px', fontSize: '0.82rem' }}
                              onClick={() => setAddingLink(true)}>
                              + Link Ekle
                            </button>
                          ) : (
                            <div className="a-link-form">
                              <input
                                className="a-search"
                                placeholder="Başlık (örn: Hafta 1 Ders Videosu)"
                                value={linkForm.title}
                                onChange={e => setLinkForm(f => ({ ...f, title: e.target.value }))}
                              />
                              <input
                                className="a-search"
                                placeholder="URL (https://...)"
                                value={linkForm.url}
                                onChange={e => setLinkForm(f => ({ ...f, url: e.target.value }))}
                              />
                              <button className="a-btn a-btn-save" onClick={() => handleAddLink(c.id!)}>Ekle</button>
                              <button className="a-btn a-btn-cancel" onClick={() => { setAddingLink(false); setLinkForm({ title: '', url: '' }); }}>İptal</button>
                            </div>
                          )}

                          <div className="a-material-list">
                            {(courseLinks[c.id!] || []).length === 0 ? (
                              <span className="a-material-empty">Henüz materyal eklenmedi</span>
                            ) : (courseLinks[c.id!] || []).map((l, i) => (
                              <div className="a-material-item" key={i}>
                                <span>{isYouTube(l.url) ? '▶️' : '🔗'}</span>
                                <a href={l.url} target="_blank" rel="noreferrer" className="a-material-name">{l.title}</a>
                                <button className="a-material-del" onClick={() => handleDeleteLink(c.id!, l.url)}>✕</button>
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default CoursesTab;
